import { useCallback } from 'react';

import { FILE_TASK_LINK_KEY, InboxEntryStatus } from '@/src/components/project/Inbox2/types';
import { queryClient } from '@/src/lib/react-query/react-query';

import { useDuplicateFile } from './api/duplicateFile';
import { activityLogsKeys } from './api/getActivityLogs';
import { fileDetailsKeys } from './api/getFileDetails';
import { useMarkMiscellaneous } from './api/markMiscellaneous';
import { useMarkRead } from './api/markRead';
import { useRecordFile } from './api/recordFile';
import { useRenameFile } from './api/renameFile';
import { useTagFileWorkstream } from './api/tagFileWorkstream';
import { useUpdateFileStatus } from './api/updateFileStatus';
import { SidebarActions } from './store/fileEntryViewer.sidebarActions.store';

import { getWebappFileViewerPageUrl } from '../../../pages/InboxEntryViewerPage/FileEntryViewerPage/WebappFileEntryViewerPage';
import useInboxSearchParams from '../../../useInboxSearchParams';

export type useSidebarActionsProps = {
  fileId: number;
  inboxEntryId: number;
  projectId?: string;
};

const useSidebarActions = ({
  // fileId,
  inboxEntryId,
  projectId = null,
}: useSidebarActionsProps): SidebarActions => {
  const { filter } = useInboxSearchParams();
  const recordFileMutation = useRecordFile({
    fileId: inboxEntryId,
  });
  const markMiscellaneousMutation = useMarkMiscellaneous({
    fileId: inboxEntryId,
  });
  const markReadMutation = useMarkRead({
    fileId: inboxEntryId,
    projectId,
  });
  const markReadMutationListener = useMarkRead({
    fileId: inboxEntryId,
    projectId,
  });
  const updateStatusMutation = useUpdateFileStatus({
    fileId: inboxEntryId,
    projectId,
  });
  const updateStatusMutationListener = useUpdateFileStatus({
    fileId: inboxEntryId,
    projectId,
  });
  const tagFileWorkstreamMutation = useTagFileWorkstream({
    fileId: inboxEntryId,
  });
  const renameFileMutation = useRenameFile({
    fileId: inboxEntryId,
    projectId,
  });
  const duplicateFileMutation = useDuplicateFile({
    fileId: inboxEntryId,
    projectId,
    filter,
  });

  const handleTaskCreated = useCallback(
    (tasks) => {
      const newTasks = tasks.map((task) => ({
        id: task.id,
        name: task.task,
        status: task.status,
      }));
      const newWorkstreams = tasks.map((task) => ({
        id: task.workstream.id,
        name: task.workstream.workstream,
        color: task.workstream.color,
      }));

      queryClient.setQueryData(
        [...fileDetailsKeys.detail({ fileId: inboxEntryId })],
        (prev: any) => {
          return {
            ...prev,
            data: {
              ...prev.data,
              'allow-close': {
                allowed: true,
              },
              'allowed-status-changes': [InboxEntryStatus.Closed],
              tasks:
                prev.data.tasks.findIndex((t) => t.id === newTasks[0].id) >= 0
                  ? prev.data.tasks.map((task) => {
                      const newTask = newTasks.find((t) => t.id === task.id);
                      if (newTask) {
                        return {
                          id: newTask.id,
                          name: newTask.task,
                          status: newTask.status,
                        };
                      }

                      return task;
                    })
                  : [...prev.data.tasks, ...newTasks],
              newWorkstreams:
                prev.data.workstreams.findIndex((t) => t.id === newTasks[0].id) >= 0
                  ? prev.data.workstreams
                  : [...prev.data.workstreams, ...newWorkstreams],
            },
          };
        }
      );

      queryClient.invalidateQueries([...activityLogsKeys.list(inboxEntryId)]);
    },
    [inboxEntryId]
  );
  const handleTaskUpdated = useCallback(
    (updatedTask) => {
      queryClient.setQueryData(
        [...fileDetailsKeys.detail({ fileId: inboxEntryId })],
        (prev: any) => {
          return {
            ...prev,
            data: {
              ...prev.data,
              tasks: prev.data.tasks.map((task) => {
                if (task.id === updatedTask.id) {
                  return {
                    id: updatedTask.id,
                    name: updatedTask.task,
                    status: updatedTask.status,
                  };
                }

                return task;
              }),
              newWorkstreams:
                prev.data.workstreams.findIndex((t) => t.id === updatedTask.workstream.id) >= 0
                  ? prev.data.workstreams
                  : [
                      ...prev.data.workstreams,
                      {
                        id: updatedTask.workstream.id,
                        name: updatedTask.workstream.workstream,
                        color: updatedTask.workstream.color,
                      },
                    ],
            },
          };
        }
      );

      queryClient.invalidateQueries([...activityLogsKeys.list(inboxEntryId)]);
    },
    [inboxEntryId]
  );

  const handleTaskDeleted = useCallback(
    ({ deletedTaskIds }) => {
      queryClient.setQueryData(
        [...fileDetailsKeys.detail({ fileId: inboxEntryId })],
        (prev: any) => {
          return {
            ...prev,
            data: {
              ...prev.data,
              tasks: prev.data.tasks.filter((task) => !deletedTaskIds.includes(task.id)),
              shouldWaitSocketUpdate: true,
            },
          };
        }
      );

      queryClient.invalidateQueries([...activityLogsKeys.list(inboxEntryId)]);
    },
    [inboxEntryId]
  );

  const handleFileArchived = useCallback(
    ({ newFolderPath, tempBreadCrumbs }) => {
      queryClient.setQueryData(
        [...fileDetailsKeys.detail({ fileId: inboxEntryId })],
        (prev: any) => {
          return {
            ...prev,
            data: {
              ...prev.data,
              'allow-close': {
                allowed: true,
              },
              'allowed-status-changes': [InboxEntryStatus.Closed],
              'library-location': {
                ...prev.data['library-location'],
                'full-path': newFolderPath,
                tempBreadCrumbs,
              },
            },
          };
        }
      );

      queryClient.invalidateQueries([...activityLogsKeys.list(inboxEntryId)]);
    },
    [inboxEntryId]
  );

  const handleActivityLogsUpdated = useCallback(() => {
    queryClient.invalidateQueries([...activityLogsKeys.list(inboxEntryId)]);
  }, [inboxEntryId]);

  const handleCopyFilelUrl = useCallback(() => {
    return getWebappFileViewerPageUrl({ projectId, inboxEntryId });
  }, [inboxEntryId, projectId]);

  return {
    recordFileMutation,
    markMiscellaneousMutation,
    markReadMutation,
    markReadMutationListener,
    updateStatusMutation,
    updateStatusMutationListener,
    tagFileWorkstreamMutation,
    renameFileMutation,
    duplicateFileMutation,
    createTask: {
      linkId: inboxEntryId,
      taskLinkKey: FILE_TASK_LINK_KEY.INBOX_DOCUMENT_ID,
      onTaskCreated: handleTaskCreated,
      onTaskUpdated: handleTaskUpdated,
      onTaskDeleted: handleTaskDeleted,
    },
    onFileArchived: handleFileArchived,
    onCopyFileUrl: handleCopyFilelUrl,
    onFileUrlCopied: handleActivityLogsUpdated,
    onFileUrlDownloaded: handleActivityLogsUpdated,
  };
};

export default useSidebarActions;
